import { useTranslations } from '@vocab/react';
import {
  Actions,
  Box,
  Button,
  ButtonIcon,
  Divider,
  Heading,
  IconEdit,
  Stack,
  Strong,
  Text,
} from 'braid-design-system';
import { useRef } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import {
  ROLE_REQUIREMENT_NAMESPACE,
  RoleRequirementsForm,
} from 'src/components/RoleRequirementsForm/RoleRequirementsForm';
import {
  isFreeTextQuestion,
  isMultipleChoiceQuestion,
  isSingleChoiceQuestion,
  type Question,
} from 'src/components/RoleRequirementsForm/utils';
import { useAllQuestions } from 'src/hooks/useAllQuestions/useAllQuestions';
import { useDialogRouterState } from 'src/modules/use-dialog-router-state';
import { ConfirmableDrawer } from 'src/ui/ConfirmableDrawer/ConfirmableDrawer';

import translations from './RoleRequirementsSummary.vocab';

export const RoleRequirementsSummary = () => {
  const { t } = useTranslations(translations);

  const rootForm = useFormContext();
  const defaultValues = useRef({
    ...rootForm.getValues(ROLE_REQUIREMENT_NAMESPACE),
  });
  const rrForm = useForm({
    defaultValues: { [ROLE_REQUIREMENT_NAMESPACE]: defaultValues.current },
  });

  const {
    isOpen,
    open: openEmployerQuestionsForm,
    close: closeEmployerQuestionsForm,
  } = useDialogRouterState('questions');

  const { data } = useAllQuestions();
  const questions = data.filter(
    (q) =>
      isSingleChoiceQuestion(q) ||
      isMultipleChoiceQuestion(q) ||
      isFreeTextQuestion(q),
  );

  const anwserCount = Object.values(defaultValues.current).filter(
    Boolean,
  ).length;

  const isDirty = Boolean(rrForm.formState.dirtyFields);

  const handleCancel = () => {
    closeEmployerQuestionsForm();
    rrForm.reset();
  };

  const submitHandler = rrForm.handleSubmit((values) => {
    closeEmployerQuestionsForm();
    rootForm.reset({ ...rootForm.getValues(), ...values });
  });

  return (
    <Stack space="small">
      <Heading level="3">{t('Question from employer')}</Heading>
      {questions.length > 0 && (
        <>
          {/* ConfirmableDrawer is acting as the space between header and content */}
          <ConfirmableDrawer
            width="small"
            name="employer-questions"
            title={t('Question from employer')}
            open={isOpen}
            shouldConfirmBeforeClose={isDirty}
            onClose={handleCancel}
          >
            <FormProvider {...rrForm}>
              <RoleRequirementsForm
                onSubmit={submitHandler}
                questions={questions as Question[]}
                actions={
                  <Actions>
                    <Button
                      type="submit"
                      tone="formAccent"
                      data={{ testid: 'save-employer-questions' }}
                    >
                      {t('Save')}
                    </Button>
                    <Button
                      type="reset"
                      tone="formAccent"
                      variant="transparent"
                      onClick={handleCancel}
                      data={{ testid: 'cancel-employer-questions' }}
                    >
                      {t('Cancel')}
                    </Button>
                  </Actions>
                }
              />
            </FormProvider>
          </ConfirmableDrawer>
          <Stack space="small">
            <Box display="flex" width="full" justifyContent="spaceBetween">
              <Text data={{ automation: 'text-answered' }}>
                {t('You answered', {
                  answered: String(anwserCount),
                  total: String(questions.length),
                  Strong: (v) => <Strong>{v}</Strong>,
                })}
              </Text>
              <ButtonIcon
                id="edit-resume"
                onClick={openEmployerQuestionsForm}
                icon={<IconEdit />}
                variant="transparent"
                label={t('Edit employer questions')}
                data={{
                  testid: 'review-role-req',
                  automation: 'button-review-role-req',
                }}
              />
            </Box>
            <Divider />
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default RoleRequirementsSummary;
